import { Link } from "react-router-dom";
import { MessageCircle, Building2, GraduationCap, Mail, Phone } from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import SectionHeading from "../components/blocks/SectionHeading";
import EnquiryForm from "../components/blocks/EnquiryForm";
import { CONTACT } from "../config/site";

const GENERAL_FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone", type: "tel" },
  { name: "organisation", label: "Organisation" },
  { name: "topic", label: "Topic", type: "select", required: true, options: ["General enquiry", "Programme enquiry", "Corporate capability", "Knowledge & research", "Events", "Other"] },
  { name: "message", label: "Message", type: "textarea", full: true, required: true },
];

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Contact — Talk to Vantage | The Vantage Institute™"
        description="Talk to The Vantage Institute: general enquiries, programme guidance, corporate capability conversations and event briefings."
        path="/contact"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />

      <section className="bg-navy">
        <div className="container-x py-16 sm:py-20">
          <p className="eyebrow-light">Contact</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            Talk to Vantage.
          </h1>
          <span className="gold-rule mt-8 !w-24" aria-hidden="true" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-platinum md:text-lg">
            Whether you are exploring a programme, building organisational capability or simply want to understand
            the Institute — the conversation starts here.
          </p>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="contact-options">
        <div className="grid gap-5 sm:grid-cols-3">
          <div className="card card-hover" data-testid="contact-option-corporate">
            <Building2 size={24} className="text-gold-deep" aria-hidden="true" />
            <h3 className="mt-4 font-sub text-base font-semibold text-navy">Corporate Enquiries</h3>
            <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate">
              Building capability across a team or enterprise? Request a corporate proposal.
            </p>
            <Link to="/organisations#enquiry" className="text-link mt-5 inline-flex min-h-[44px] items-center" data-testid="contact-cta-corporate">
              Corporate Enquiry
            </Link>
          </div>
          <div className="card card-hover" data-testid="contact-option-programme">
            <GraduationCap size={24} className="text-gold-deep" aria-hidden="true" />
            <h3 className="mt-4 font-sub text-base font-semibold text-navy">Programme Enquiries</h3>
            <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate">
              Exploring the catalogue? Browse programmes, or ask us for guidance below.
            </p>
            <Link to="/programmes" className="text-link mt-5 inline-flex min-h-[44px] items-center" data-testid="contact-cta-programmes">
              Explore Programmes
            </Link>
          </div>
          <div className="card card-hover" data-testid="contact-option-whatsapp">
            <MessageCircle size={24} className="text-gold-deep" aria-hidden="true" />
            <h3 className="mt-4 font-sub text-base font-semibold text-navy">WhatsApp</h3>
            <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate">
              Message the Institute directly on WhatsApp for a fast, personal response.
            </p>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="text-link mt-5 inline-flex min-h-[44px] items-center" data-testid="contact-cta-whatsapp">
              WhatsApp Vantage
            </a>
          </div>
        </div>
        <div className="mt-8 flex items-center gap-4" data-testid="contact-direct">
          <a
            href={CONTACT.phoneHref}
            aria-label="Call The Vantage Institute"
            title="Call The Vantage Institute"
            data-testid="contact-phone"
            className="flex min-h-[48px] min-w-[48px] items-center justify-center border border-platinum bg-white text-navy transition-colors hover:border-gold hover:text-gold-deep"
          >
            <Phone size={20} aria-hidden="true" />
          </a>
          <a
            href={`mailto:${CONTACT.email}`}
            aria-label="Email The Vantage Institute"
            title="Email The Vantage Institute"
            data-testid="contact-email"
            className="flex min-h-[48px] min-w-[48px] items-center justify-center border border-platinum bg-white text-navy transition-colors hover:border-gold hover:text-gold-deep"
          >
            <Mail size={20} aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="bg-paper" data-testid="contact-form-section">
        <div className="container-x py-16 sm:py-24">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
            <SectionHeading
              eyebrow="General Enquiry"
              title="Send us a message."
              intro="Complete the form and your enquiry opens pre-filled in your email or WhatsApp, addressed directly to the Institute."
            />
            <div className="card">
              <EnquiryForm fields={GENERAL_FIELDS} submitLabel="Submit Enquiry" testId="general-enquiry-form" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
